import { useMutation } from "@apollo/client";
import { Box, Link, Stack, Typography } from "@mui/material";
import { APIProvider, Circle, Map as GoogleMap, Marker, useMap } from "@vis.gl/react-google-maps";
import moment from "moment";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import homeMarkerIcon from "../../../../assets/icons/home-marker.svg";
import technicianCarMarkerIcon from "../../../../assets/icons/technician-car-marker.svg";
import installCompleteIcon from "../../../../assets/icons/tracker/install-complete.svg";
import installDayIcon from "../../../../assets/icons/tracker/install-day.svg";
import installScheduledIcon from "../../../../assets/icons/tracker/install-scheduled.svg";
import techArrivedIcon from "../../../../assets/icons/tracker/tech-arrived.svg";
import techOnTheWayIcon from "../../../../assets/icons/tracker/tech-on-the-way.svg";
import {
  isAdmin,
  TECHNICIAN_ARRIVED_HEADING,
  TECHNICIAN_ARRIVED_MAP_MESSAGE,
  TECHNICIAN_ARRIVED_MESSAGE,
  TECHNICIAN_ON_THE_WAY_HEADING,
  TECHNICIAN_ON_THE_WAY_MESSAGE,
  TECHNICIAN_NO_ETA_MESSAGE,
  WHERE_IS_MY_TECH_STATUS_KEYS,
} from "../../../../Constants/constants";
import { setIsOrder } from "../../../../Redux/Slices/OrderDetailsSlice";
import { GET_TECHNICIAN_STATUS } from "../../../../Routes/query";
import { STEP_ICON_ACTIVE_COLOR, TrackerSteps } from "./TrackerSteps";
import {
  getTechOnTheWayDescription,
  getTechTrackerStatusContent,
} from "../../../../Services/helper";

type InstallationOrderDetails = {
  orderId?: string | null;
  workOrderNumber?: string | null;
  installationAppointmentStartDate?: string | null;
  installationAppointmentEndDate?: string | null;
  isInstallationDay?: boolean | null;
  orderStatus?: string | null;
  addressInfo?: {
    serviceAddress?: string | null;
    latitude?: string | number | null;
    longitude?: string | number | null;
  } | null;
  orderReschedule?: boolean | null;
  checkMap?: boolean | string | null;
  showTechnicianTracker?: boolean | string | null;
  technicianDetails?: {
    name?: string | null;
  } | null;
};

type InstallationStatusTrackerProps = {
  orderDetails?: InstallationOrderDetails | null;
};

type TechnicianStatusResponse = {
  APIStatus?: string | null;
  error?: boolean | string | null;
  message?: string | null;
  longitude?: string | number | null;
  latitude?: string | number | null;
  orderStatus?: string | number | null;
  workOrderNumber?: string | null;
  estimatedTravelTime?: string | number | null;
  estimatedArrivalTime?: string | null;
};

type LatLng = { lat: number; lng: number };

type GoogleMapsWindow = Window &
  typeof globalThis & {
    google?: {
      maps?: {
        Size?: new (width: number, height: number) => unknown;
        Point?: new (x: number, y: number) => unknown;
      };
    };
  };

type MapsAuthWindow = Window & { gm_authFailure?: () => void };

type MapLoadStatus = "loading" | "ready" | "failed";

const DEFAULT_PRIVACY_RADIUS_MILES = 1.5;
const PRIVACY_RADIUS_SHRINK_THRESHOLD_MILES = 2.5;
const MIN_PRIVACY_RADIUS_MILES = 0.25;
const MILES_TO_METERS = 1609.34;
const METERS_PER_DEGREE_LATITUDE = 111320;
const MAP_MARKER_SIZE_PX = 44;
// Home marker: white circle, navy border and navy home icon, 36px (same as the mobile app).
const HOME_MARKER_SIZE_PX = 36;
// The circle is never drawn smaller than this on screen, so it always clearly surrounds the car.
const MIN_CIRCLE_RADIUS_PX = 70;
const TECHNICIAN_STATUS_POLL_INTERVAL_MS = 5 * 60 * 1000;
// Buffer added to the technician ETA so we don't promise an arrival that's too early.
const ETA_BUFFER_MINUTES = 10;
// If the map hasn't drawn its first tiles by then (e.g. a stalled request), it is hidden.
const MAP_LOAD_TIMEOUT_MS = 20 * 1000;

// Google calls window.gm_authFailure when it rejects the API key (wrong key, domain not allowed,
// API disabled) and then shows its own error box instead of the map. It only calls it once per
// page, so the failure is remembered for maps mounted later.
let hasMapsAuthFailed = false;
let notifyMapsAuthFailure: (() => void) | null = null;
const handleMapsAuthFailure = () => {
  hasMapsAuthFailed = true;
  notifyMapsAuthFailure?.();
};
// Tests only: forget a key rejection from an earlier test.
export const resetMapsAuthFailure = () => {
  hasMapsAuthFailed = false;
};

const toRadians = (value: number) => (value * Math.PI) / 180;

const calculateDistanceMiles = (
  start: { lat: number; lng: number },
  end: { lat: number; lng: number }
) => {
  const earthRadiusMiles = 3958.8;
  const latitudeDelta = toRadians(end.lat - start.lat);
  const longitudeDelta = toRadians(end.lng - start.lng);
  const startLatitude = toRadians(start.lat);
  const endLatitude = toRadians(end.lat);
  const haversineValue =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) *
      Math.sin(longitudeDelta / 2);
  const angularDistance = 2 * Math.atan2(Math.sqrt(haversineValue), Math.sqrt(1 - haversineValue));

  return earthRadiusMiles * angularDistance;
};

export const getPrivacyRadiusMeters = (
  technicianLocation: { lat: number; lng: number },
  homeLocation: { lat: number; lng: number } | null
) => {
  const defaultRadiusMeters = DEFAULT_PRIVACY_RADIUS_MILES * MILES_TO_METERS;

  if (!homeLocation) {
    return defaultRadiusMeters;
  }

  const distanceMiles = calculateDistanceMiles(technicianLocation, homeLocation);

  if (distanceMiles >= PRIVACY_RADIUS_SHRINK_THRESHOLD_MILES) {
    return defaultRadiusMeters;
  }

  const shrunkRadiusMiles = Math.max(MIN_PRIVACY_RADIUS_MILES, distanceMiles / 2);
  return Math.min(DEFAULT_PRIVACY_RADIUS_MILES, shrunkRadiusMiles) * MILES_TO_METERS;
};

// Scaled icon once the Maps API is loaded; the plain URL is a safe fallback.
const buildMarkerIcon = (url: string, size: number = MAP_MARKER_SIZE_PX) => {
  const googleMaps = (window as GoogleMapsWindow).google?.maps;

  if (!googleMaps?.Size || !googleMaps?.Point) {
    return url;
  }

  return {
    url,
    scaledSize: new googleMaps.Size(size, size),
    anchor: new googleMaps.Point(size / 2, size / 2),
  } as google.maps.Icon;
};

// Keeps the whole technician circle and the home marker in view.
const FitTechnicianAndHome = ({
  technicianLocation,
  radiusMeters,
  homeLocation,
}: {
  technicianLocation: LatLng;
  radiusMeters: number;
  homeLocation: LatLng | null;
}) => {
  const map = useMap();
  const { lat, lng } = technicianLocation;
  const homeLat = homeLocation?.lat;
  const homeLng = homeLocation?.lng;

  useEffect(() => {
    if (!map) {
      return;
    }

    const latitudeDelta = radiusMeters / METERS_PER_DEGREE_LATITUDE;
    const longitudeDelta = radiusMeters / (METERS_PER_DEGREE_LATITUDE * Math.cos(toRadians(lat)));
    const latitudes = [lat - latitudeDelta, lat + latitudeDelta];
    const longitudes = [lng - longitudeDelta, lng + longitudeDelta];

    if (typeof homeLat === "number" && typeof homeLng === "number") {
      latitudes.push(homeLat);
      longitudes.push(homeLng);
    }

    map.fitBounds(
      {
        north: Math.max(...latitudes),
        south: Math.min(...latitudes),
        east: Math.max(...longitudes),
        west: Math.min(...longitudes),
      },
      // Room for the ETA popup above the circle, and for the circle's minimum on-screen size.
      { top: 160, right: 80, bottom: 80, left: 80 }
    );
  }, [map, lat, lng, radiusMeters, homeLat, homeLng]);

  return null;
};

// ETA bubble pinned to the top edge of the technician's circle, with its arrow pointing at the
// circle. It moves with the technician.
const TechnicianEtaPopup = ({
  anchor,
  title,
  subtitle,
}: {
  anchor: LatLng;
  title: string;
  subtitle?: string;
}) => {
  const map = useMap();
  const [container] = useState(() => document.createElement("div"));
  const positionRef = useRef(anchor);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  positionRef.current = anchor;

  useEffect(() => {
    if (!map || !window.google?.maps?.OverlayView) {
      return;
    }

    const overlay = new window.google.maps.OverlayView();
    // Below the marker layer, so the home and car markers are never hidden by the popup.
    overlay.onAdd = () => overlay.getPanes()?.overlayLayer.appendChild(container);
    overlay.draw = () => {
      const point = overlay
        .getProjection()
        ?.fromLatLngToDivPixel(new window.google.maps.LatLng(positionRef.current));

      if (point) {
        container.style.position = "absolute";
        container.style.left = `${point.x}px`;
        container.style.top = `${point.y}px`;
      }
    };
    overlay.onRemove = () => container.remove();
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, container]);

  useEffect(() => {
    overlayRef.current?.draw();
  }, [anchor.lat, anchor.lng]);

  return createPortal(
    <Box
      role="status"
      sx={{
        position: "absolute",
        left: 0,
        bottom: 12,
        transform: "translateX(-50%)",
        width: "max-content",
        maxWidth: 320,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: "#fff",
        boxShadow: "0 8px 20px rgba(23, 23, 23, 0.2)",
        textAlign: "center",
        pointerEvents: "none",
        "&::after": {
          position: "absolute",
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          borderTop: "8px solid #fff",
          borderRight: "8px solid transparent",
          borderLeft: "8px solid transparent",
          content: '""',
        },
      }}>
      <MapMessageText title={title} subtitle={subtitle} />
    </Box>,
    container
  );
};

// Title and optional subtitle shown on the map, in the ETA popup or the banner below.
const MapMessageText = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <>
    <Typography
      component="p"
      sx={{ m: 0, color: "#171717", fontSize: 15, fontWeight: 700, lineHeight: 1.25 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography
        component="p"
        sx={{ mt: 0.5, mb: 0, color: "#006ce5", fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
        {subtitle}
      </Typography>
    )}
  </>
);

// The map message as a banner across the top of the map when there is no technician location to
// pin it to (the technician API sends none once they arrive), like the mobile app.
const MapMessageBanner = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Box
    role="status"
    sx={{
      position: "absolute",
      top: 12,
      left: "50%",
      zIndex: 1,
      transform: "translateX(-50%)",
      width: "max-content",
      maxWidth: "calc(100% - 24px)",
      px: 2,
      py: 1.25,
      borderRadius: 2,
      backgroundColor: "#fff",
      boxShadow: "0 8px 20px rgba(23, 23, 23, 0.2)",
      textAlign: "center",
      pointerEvents: "none",
    }}>
    <MapMessageText title={title} subtitle={subtitle} />
  </Box>
);

// Car marker in the middle of its circle, with the ETA popup on top of the circle. The circle is
// the privacy radius, grown to at least MIN_CIRCLE_RADIUS_PX on screen when zoomed out (like the
// mobile app), but never reaching more than 60% of the way to the home marker.
const TechnicianMarkerGroup = ({
  technicianLocation,
  homeLocation,
  privacyRadiusMeters,
  etaTitle,
  etaSubtitle,
}: {
  technicianLocation: LatLng;
  homeLocation: LatLng | null;
  privacyRadiusMeters: number;
  etaTitle: string;
  etaSubtitle?: string;
}) => {
  const map = useMap();
  const [zoom, setZoom] = useState<number | undefined>(() => map?.getZoom());

  useEffect(() => {
    if (!map) {
      return;
    }

    setZoom(map.getZoom());
    const listener = map.addListener("zoom_changed", () => setZoom(map.getZoom()));
    return () => listener.remove();
  }, [map]);

  const metersPerPixel =
    typeof zoom === "number"
      ? (156543.03392 * Math.cos(toRadians(technicianLocation.lat))) / Math.pow(2, zoom)
      : 0;
  const maxRadiusMeters = homeLocation
    ? Math.max(calculateDistanceMiles(technicianLocation, homeLocation) * MILES_TO_METERS * 0.6, 150)
    : Infinity;
  const circleRadiusMeters = Math.min(
    Math.max(privacyRadiusMeters, MIN_CIRCLE_RADIUS_PX * metersPerPixel),
    maxRadiusMeters
  );
  const circleTop = {
    lat: technicianLocation.lat + circleRadiusMeters / METERS_PER_DEGREE_LATITUDE,
    lng: technicianLocation.lng,
  };

  return (
    <>
      <Circle
        center={technicianLocation}
        radius={circleRadiusMeters}
        strokeColor="#1a73e8"
        strokeOpacity={0.85}
        strokeWeight={2}
        fillColor="#1a73e8"
        fillOpacity={0.2}
      />
      <Marker
        position={technicianLocation}
        title="Technician location"
        icon={buildMarkerIcon(technicianCarMarkerIcon)}
        zIndex={2}
      />
      <TechnicianEtaPopup anchor={circleTop} title={etaTitle} subtitle={etaSubtitle} />
    </>
  );
};

// Memoised on primitive coordinates and ETA text, so a poll that returns the same position
// and ETA doesn't re-render the map. The map instance itself is created once per mount, which
// is what Google bills for; position changes just move the markers and the ETA bubble.
const TechnicianMapCanvas = memo(
  ({
    apiKey,
    latitude,
    longitude,
    homeLatitude,
    homeLongitude,
    etaTitle,
    etaSubtitle,
    onReady,
    onError,
  }: {
    apiKey: string;
    latitude: number | null;
    longitude: number | null;
    homeLatitude: number | null;
    homeLongitude: number | null;
    etaTitle: string;
    etaSubtitle?: string;
    onReady: () => void;
    onError: () => void;
  }) => {
    const technicianLocation =
      latitude !== null && longitude !== null ? { lat: latitude, lng: longitude } : null;
    const homeLocation =
      homeLatitude !== null && homeLongitude !== null
        ? { lat: homeLatitude, lng: homeLongitude }
        : null;
    // Without the technician's location the map shows just the home.
    const center = technicianLocation ?? homeLocation;

    if (!center) {
      return null;
    }

    const privacyRadiusMeters = technicianLocation
      ? getPrivacyRadiusMeters(technicianLocation, homeLocation)
      : 0;

    return (
      <APIProvider apiKey={apiKey} onError={onError}>
        <GoogleMap
          defaultCenter={center}
          defaultZoom={14}
          disableDefaultUI
          clickableIcons={false}
          gestureHandling="cooperative"
          onTilesLoaded={onReady}
          style={{ width: "100%", height: "100%" }}>
          {technicianLocation && (
            <TechnicianMarkerGroup
              technicianLocation={technicianLocation}
              homeLocation={homeLocation}
              privacyRadiusMeters={privacyRadiusMeters}
              etaTitle={etaTitle}
              etaSubtitle={etaSubtitle}
            />
          )}
          {homeLocation && (
            <Marker
              position={homeLocation}
              title="Service address"
              icon={buildMarkerIcon(homeMarkerIcon, HOME_MARKER_SIZE_PX)}
              zIndex={1}
            />
          )}
          {technicianLocation && (
            <FitTechnicianAndHome
              technicianLocation={technicianLocation}
              radiusMeters={privacyRadiusMeters}
              homeLocation={homeLocation}
            />
          )}
        </GoogleMap>
      </APIProvider>
    );
  }
);

const TechnicianLocationMap = ({
  latitude,
  longitude,
  homeLatitude,
  homeLongitude,
  estimatedArrivalTime,
  estimatedTravelTime,
  isTechArrived = false,
}: {
  latitude: number | null;
  longitude: number | null;
  homeLatitude: number | null;
  homeLongitude: number | null;
  estimatedArrivalTime?: string;
  estimatedTravelTime?: string;
  isTechArrived?: boolean;
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
  const [mapStatus, setMapStatus] = useState<MapLoadStatus>(() =>
    hasMapsAuthFailed ? "failed" : "loading"
  );
  const travelTimeLabel = estimatedTravelTime
    ? `Your technician is about ${estimatedTravelTime} away`
    : "Your technician is on the way";
  // Once the technician has arrived the map says so, without a time (same as the mobile app).
  const etaTitle = isTechArrived ? TECHNICIAN_ARRIVED_MAP_MESSAGE : travelTimeLabel;
  const etaSubtitle =
    !isTechArrived && estimatedArrivalTime ? `Estimated arrival ${estimatedArrivalTime}` : undefined;
  const hasTechnicianLocation = latitude !== null && longitude !== null;
  const hasHomeLocation = homeLatitude !== null && homeLongitude !== null;
  // The map counts as loaded only once its first tiles have drawn; any failure is final.
  const markMapReady = useCallback(
    () => setMapStatus((status) => (status === "loading" ? "ready" : status)),
    []
  );
  const markMapFailed = useCallback(() => setMapStatus("failed"), []);

  useEffect(() => {
    (window as MapsAuthWindow).gm_authFailure = handleMapsAuthFailure;
    notifyMapsAuthFailure = markMapFailed;
    return () => {
      notifyMapsAuthFailure = null;
    };
  }, [markMapFailed]);

  useEffect(() => {
    if (mapStatus !== "loading") {
      return undefined;
    }
    const timer = setTimeout(markMapFailed, MAP_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [mapStatus, markMapFailed]);

  // A map that can't load is left out entirely, rather than showing an error in its place.
  if (!apiKey || (!hasTechnicianLocation && !hasHomeLocation) || mapStatus === "failed") {
    return null;
  }

  const isMapReady = mapStatus === "ready";

  return (
    <Box sx={{ px: 1.25, pb: 1.25, backgroundColor: "#fff" }}>
      <Box
        component="section"
        aria-label="Technician location map"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: 340, sm: 430 },
          overflow: "hidden",
          borderRadius: 4,
          backgroundColor: "#dbe7fb",
          boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.06)",
        }}>
        {/* Invisible until its tiles have drawn (they still load), so a half-loaded map or
            Google's own error box is never seen. */}
        <Box
          data-testid="technician-map-canvas"
          sx={{ width: "100%", height: "100%", opacity: isMapReady ? 1 : 0 }}>
          <TechnicianMapCanvas
            apiKey={apiKey}
            latitude={latitude}
            longitude={longitude}
            homeLatitude={homeLatitude}
            homeLongitude={homeLongitude}
            etaTitle={etaTitle}
            etaSubtitle={etaSubtitle}
            onReady={markMapReady}
            onError={markMapFailed}
          />
        </Box>
        {isMapReady && !hasTechnicianLocation && (
          <MapMessageBanner title={etaTitle} subtitle={etaSubtitle} />
        )}
        {!isMapReady && (
          <Box
            role="status"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
            }}>
            <Typography sx={{ color: "#666", fontSize: 14 }}>Loading map...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Technician's first initial on a royal blue circle (same as the mobile app).
const TechnicianAvatar = ({ name }: { name: string }) => (
  <Box
    aria-hidden="true"
    sx={{
      display: "flex",
      width: 56,
      height: 56,
      flex: "0 0 56px",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      backgroundColor: STEP_ICON_ACTIVE_COLOR,
      color: "#fff",
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1,
    }}>
    {String(name ?? "").trim().charAt(0).toUpperCase()}
  </Box>
);

// Fallback step labels/icons (the mobile app's icons), used when the "where-is-my-tech" banner
// has none or its icon fails to load.
const steps = [
  { label: "Install\nScheduled", icon: installScheduledIcon },
  { label: "Install Day", icon: installDayIcon },
  { label: "Tech on\nthe way", icon: techOnTheWayIcon },
  { label: "Tech Arrived", icon: techArrivedIcon },
  { label: "Install Complete", icon: installCompleteIcon },
];

const parseCoordinate = (value?: string | number | null, min?: number, max?: number) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = typeof value === "number" ? value : Number(String(value).trim());

  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  if (typeof min === "number" && parsedValue < min) {
    return null;
  }

  if (typeof max === "number" && parsedValue > max) {
    return null;
  }

  return parsedValue;
};

// Dispatch tracker steps, keyed by normalised order status (same map as the mobile app).
const DISPATCH_STEPS: Record<string, number> = {
  installationscheduled: 0,
  installday: 1,
  techontheway: 2,
  techarrived: 3,
  installcomplete: 4,
};

// Steps shown when the backend turns the technician tracker off (showTechnicianTracker false):
// no "Tech on the way" or "Tech Arrived".
const STEPS_WITHOUT_TECHNICIAN = [
  DISPATCH_STEPS.installationscheduled,
  DISPATCH_STEPS.installday,
  DISPATCH_STEPS.installcomplete,
];
const ALL_STEPS = steps.map((_, index) => index);

const normalizeOrderStatus = (value?: string | number | null) =>
  String(value ?? "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

// The technician API sends a status name ("techOntheWay"); older responses sent a number
// (below 2 = on the way, 2 and above = arrived).
const getTechnicianStep = (value?: string | number | null) => {
  const normalizedValue = normalizeOrderStatus(value);

  if (!normalizedValue) {
    return null;
  }

  if (/^\d+$/.test(normalizedValue)) {
    return Number(normalizedValue) < 2 ? DISPATCH_STEPS.techontheway : DISPATCH_STEPS.techarrived;
  }

  return DISPATCH_STEPS[normalizedValue] ?? null;
};

// The order-details status drives the tracker up to "Tech on the way" (as in the mobile app's
// resolveDispatchStep). From then on, while the order is on the way or arrived, the technician API
// decides "Tech Arrived" and "Install Complete". It only moves the tracker forward, and its status
// is ignored before "Tech on the way".
const resolveDispatchStep = (orderStep: number, technicianOrderStatus?: string | number | null) => {
  if (orderStep !== DISPATCH_STEPS.techontheway && orderStep !== DISPATCH_STEPS.techarrived) {
    return orderStep;
  }

  const technicianStep = getTechnicianStep(technicianOrderStatus);
  return technicianStep !== null && technicianStep > orderStep ? technicianStep : orderStep;
};

const getTechnicianFirstName = (value?: string | null) => {
  const trimmedValue = String(value ?? "").trim();

  if (!trimmedValue) {
    return "";
  }

  return trimmedValue.split(/\s+/).find(Boolean) ?? "";
};

// Travel time arrives as plain minutes ("19", "19 minutes"); we add a buffer on top.
export const getBufferedTravelMinutes = (value?: string | number | null) => {
  const matchedMinutes = String(value ?? "")
    .trim()
    .match(/^(\d+(?:\.\d+)?)/);

  if (!matchedMinutes) {
    return null;
  }

  const parsedMinutes = Number(matchedMinutes[1]);
  return Number.isFinite(parsedMinutes) ? Math.round(parsedMinutes) + ETA_BUFFER_MINUTES : null;
};

// Arrival time shown to the customer: the API's estimatedArrivalTime plus the ETA buffer, exactly
// as the mobile app shows it. moment.utc keeps the time as the API sent it (no shift by the
// browser's time zone), matching how every other tracker time is shown.
export const getEstimatedArrivalTime = (apiArrivalTime?: string | null) => {
  const trimmedValue = String(apiArrivalTime ?? "").trim();

  if (!trimmedValue) {
    return "";
  }

  const isoTime = moment.utc(trimmedValue, moment.ISO_8601, true);

  if (isoTime.isValid()) {
    return isoTime.add(ETA_BUFFER_MINUTES, "minutes").format("h:mm A");
  }

  const clockTime = moment.utc(trimmedValue, ["h:mmA", "h:mm A", "H:mm"], true);

  if (clockTime.isValid()) {
    return clockTime.add(ETA_BUFFER_MINUTES, "minutes").format("h:mm A");
  }

  return trimmedValue.length <= 20 ? trimmedValue : "";
};

const formatAppointment = (startValue?: string | null, endValue?: string | null) => {
  const start = startValue ? moment.utc(startValue) : null;
  const end = endValue ? moment.utc(endValue) : null;
  const validStart = start?.isValid() ? start : null;
  const validEnd = end?.isValid() ? end : null;

  if (validStart && validEnd) {
    const startText = validStart.format("MMM D, h:mm A");
    const endText = validStart.isSame(validEnd, "day")
      ? validEnd.format("h:mm A")
      : validEnd.format("MMM D, h:mm A");
    return `${startText} – ${endText}`;
  }

  return validStart?.format("MMM D, h:mm A") ?? "—";
};

export const InstallationStatusTracker = ({
  orderDetails: orderDetailsProp,
}: InstallationStatusTrackerProps) => {
  // A missing order renders the first step with its default copy instead of crashing.
  const orderDetails: InstallationOrderDetails = orderDetailsProp ?? {};
  const loginEmail = useSelector((state: any) => state.userInfo?.email);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Same redirect as the previous order tracker's "Reschedule appointment" button: the reschedule
  // page, flagged as an order (not a trouble ticket).
  const handleReschedule = () => {
    dispatch(setIsOrder(true));
    // Also in the history state, so the reschedule page still knows it's an order after a refresh.
    navigate("/home/reschedule-appointment", { state: { isOrder: true } });
  };
  // Same account the order was fetched for (the order-details V2 call uses the selected account).
  const accountNumber = useSelector((state: any) =>
    String(state?.customerInfoDetails?.accountNumber ?? "").trim()
  );
  const workOrderNumber = String(orderDetails.workOrderNumber ?? "").trim();
  const orderStatus = String(orderDetails.orderStatus ?? "").trim();
  const isCheckMapEnabled =
    orderDetails.checkMap === true || String(orderDetails.checkMap).toLowerCase() === "true";
  const showTechnicianTracker =
    orderDetails.showTechnicianTracker === true ||
    String(orderDetails.showTechnicianTracker).toLowerCase() === "true";
  // Last good technician response; a failed poll keeps the previous one instead of wiping it.
  const [technicianStatus, setTechnicianStatus] = useState<TechnicianStatusResponse>();
  const [getTechnicianStatus] = useMutation(GET_TECHNICIAN_STATUS, {
    fetchPolicy: "no-cache",
  });

  // Same email the order-details V2 call uses (and the mobile app sends): the login email,
  // or the impersonated customer's email in the admin app.
  const email = String(
    (isAdmin ? localStorage.getItem("impersonatedEmail") : loginEmail) ?? ""
  ).trim();
  const orderStep = DISPATCH_STEPS[normalizeOrderStatus(orderStatus)] ?? -1;
  const currentStep = resolveDispatchStep(orderStep, technicianStatus?.orderStatus);
  const isTechnicianStep =
    currentStep === DISPATCH_STEPS.techontheway || currentStep === DISPATCH_STEPS.techarrived;
  // Without the technician tracker, an on-the-way or arrived order stays on Install Day.
  const isTechOnTheWay = showTechnicianTracker && currentStep === DISPATCH_STEPS.techontheway;
  const isTechArrived = showTechnicianTracker && currentStep === DISPATCH_STEPS.techarrived;
  const isInstallationComplete = currentStep === DISPATCH_STEPS.installcomplete;
  // The technician API is only called once the order details say the technician is on the way,
  // never before. It keeps polling after the technician arrives, so the map stays current there
  // too (as in the mobile app), and stops once the installation is complete.
  const shouldTrackTechnician =
    showTechnicianTracker &&
    isCheckMapEnabled &&
    Boolean(workOrderNumber) &&
    Boolean(email) &&
    Boolean(accountNumber) &&
    (orderStep === DISPATCH_STEPS.techontheway || orderStep === DISPATCH_STEPS.techarrived) &&
    !isInstallationComplete;

  // Polls while this tracker is mounted, which only happens on the Home screen. Leaving Home
  // unmounts it, and the cleanup stops the timer.
  useEffect(() => {
    if (!shouldTrackTechnician) {
      return;
    }

    let isActive = true;
    const fetchTechnicianStatus = () => {
      getTechnicianStatus({ variables: { email, accountNumber, workOrderNumber } })
        .then((response) => {
          const nextStatus: TechnicianStatusResponse | undefined =
            response.data?.getTechnicianStatus;

          if (isActive && nextStatus && !nextStatus.error) {
            setTechnicianStatus(nextStatus);
          }
        })
        .catch(() => undefined);
    };

    fetchTechnicianStatus();
    const pollTimer = window.setInterval(fetchTechnicianStatus, TECHNICIAN_STATUS_POLL_INTERVAL_MS);

    return () => {
      isActive = false;
      window.clearInterval(pollTimer);
    };
  }, [accountNumber, email, getTechnicianStatus, shouldTrackTechnician, workOrderNumber]);

  const isInstallationDay =
    orderDetails.isInstallationDay === true || (!showTechnicianTracker && isTechnicianStep);
  const activeStep = isInstallationComplete
    ? 4
    : isTechArrived
      ? 3
      : isTechOnTheWay
        ? 2
        : isInstallationDay
          ? 1
          : 0;
  const appointment = formatAppointment(
    orderDetails.installationAppointmentStartDate,
    orderDetails.installationAppointmentEndDate
  );
  const technicianFirstName = getTechnicianFirstName(orderDetails.technicianDetails?.name);
  const latitude = parseCoordinate(technicianStatus?.latitude, -90, 90);
  const longitude = parseCoordinate(technicianStatus?.longitude, -180, 180);
  const homeLatitude = parseCoordinate(orderDetails.addressInfo?.latitude, -90, 90);
  const homeLongitude = parseCoordinate(orderDetails.addressInfo?.longitude, -180, 180);
  const bufferedTravelMinutes = getBufferedTravelMinutes(technicianStatus?.estimatedTravelTime);
  const estimatedArrivalTime = getEstimatedArrivalTime(technicianStatus?.estimatedArrivalTime);
  const estimatedTravelTime = bufferedTravelMinutes !== null ? `${bufferedTravelMinutes} min` : "";
  const hasTechnicianLocation = latitude !== null && longitude !== null;
  const hasHomeLocation = homeLatitude !== null && homeLongitude !== null;
  // Like the mobile app: while the technician is on the way the map needs their location and a
  // travel or arrival time. Once they arrive the technician API sends no location or time, so
  // the map shows the home with "Your technician has arrived" (at the technician's location
  // instead when the API does send one).
  const showMap =
    !isInstallationComplete &&
    ((isTechOnTheWay &&
      hasTechnicianLocation &&
      Boolean(estimatedArrivalTime || estimatedTravelTime)) ||
      (isTechArrived &&
        isCheckMapEnabled &&
        Boolean(workOrderNumber) &&
        (hasTechnicianLocation || hasHomeLocation)));
  const bannerMessages = useSelector((state: any) => state?.sideCoreContent?.bannerMessages);
  const stepContent = WHERE_IS_MY_TECH_STATUS_KEYS.map((key) =>
    getTechTrackerStatusContent(bannerMessages, key, { estimatedArrivalTime })
  );
  const trackerSteps = steps.map((step, index) => ({
    label: stepContent[index]?.orderStatusLabel || step.label,
    icon: stepContent[index]?.iconSrc || step.icon,
    fallbackIcon: step.icon,
  }));
  const visibleSteps = showTechnicianTracker ? ALL_STEPS : STEPS_WITHOUT_TECHNICIAN;
  const currentContent = stepContent[activeStep];
  const technicianName = technicianFirstName || "Your technician";
  const onTheWayHeading = `${technicianName} ${
    currentContent?.heading || TECHNICIAN_ON_THE_WAY_HEADING
  }`;
  // The CMS variant with {{estimatedArrivalTime}} when the technician API gives an arrival time,
  // the "arriving at your doorstep soon" one when it doesn't.
  const onTheWayMessage = getTechOnTheWayDescription(
    bannerMessages,
    estimatedArrivalTime,
    TECHNICIAN_ON_THE_WAY_MESSAGE,
    TECHNICIAN_NO_ETA_MESSAGE
  );
  const arrivedHeading = `${technicianName} ${currentContent?.heading || TECHNICIAN_ARRIVED_HEADING}`;
  const arrivedMessage = `${technicianName} ${
    currentContent?.subheading || TECHNICIAN_ARRIVED_MESSAGE
  }`;

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        border: "1px solid #dedede",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}>
      <TrackerSteps
        steps={visibleSteps.map((index) => trackerSteps[index])}
        activeStep={visibleSteps.indexOf(activeStep)}
        ariaLabel="Installation status"
      />

      <Box
        sx={{
          backgroundColor:
            isInstallationDay || isInstallationComplete || isTechOnTheWay || isTechArrived
              ? "#f1f6ff"
              : "#fff",
        }}>
        {isInstallationComplete ? (
          <Box sx={{ px: 2.25, py: 2.25, backgroundColor: "#f1f6ff" }}>
            <Typography
              component="h2"
              sx={{
                mb: currentContent?.subheading ? 1 : 0,
                color: "#171717",
                fontSize: 20,
                fontWeight: 700,
                lineHeight: 1.25,
              }}>
              {currentContent?.heading || "Your installation is complete"}
            </Typography>
            {currentContent?.subheading && (
              <Typography
                component="p"
                sx={{ maxWidth: 560, m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
                {currentContent?.subheading}
              </Typography>
            )}
          </Box>
        ) : isTechOnTheWay ? (
          <Box sx={{ px: 2.25, py: 2.25, backgroundColor: "#f1f6ff" }}>
            <Typography
              component="h2"
              sx={{
                mb: 1,
                color: "#171717",
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.25,
                overflowWrap: "anywhere",
              }}>
              {onTheWayHeading}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TechnicianAvatar name={technicianName} />
              <Typography
                component="p"
                sx={{ minWidth: 0, m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
                {onTheWayMessage}
              </Typography>
            </Stack>
          </Box>
        ) : isTechArrived ? (
          <Box sx={{ px: 2.25, py: 2.25, backgroundColor: "#f1f6ff" }}>
            <Typography
              component="h2"
              sx={{
                mb: 1,
                color: "#171717",
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.25,
                overflowWrap: "anywhere",
              }}>
              {arrivedHeading}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TechnicianAvatar name={technicianName} />
              <Typography
                component="p"
                sx={{ minWidth: 0, m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
                {arrivedMessage}
              </Typography>
            </Stack>
          </Box>
        ) : isInstallationDay ? (
          <Box sx={{ px: 2.25, py: 2.25, backgroundColor: "#f1f6ff" }}>
            <Typography
              component="h2"
              sx={{ mb: 1, color: "#171717", fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>
              {currentContent?.heading || "Today is your install day"}
            </Typography>
            <Typography
              component="p"
              sx={{ maxWidth: 560, m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
              {currentContent?.subheading ||
                "A technician will arrive to install your equipment, test your network and answer any questions you may have about your service."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2.25, py: 2.25, backgroundColor: "#f1f6ff" }}>
              <Typography
                component="h2"
                sx={{ mb: 1, color: "#171717", fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
                {currentContent?.heading || "Install Scheduled"}
              </Typography>
              <Typography
                component="p"
                sx={{ maxWidth: 560, m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
                {currentContent?.subheading ||
                  "Extra text to reinforce purchase decision, reminder on autopay and paperless in subhead, will live on the home page."}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0, sm: 2.25 }}
              sx={{ px: 2, pt: 1.75, backgroundColor: "#fff" }}>
              <Box sx={{ flex: 1, minWidth: 0, pb: 1.75 }}>
                <Typography
                  component="p"
                  sx={{ mb: 0.5, color: "#666", fontSize: 12, lineHeight: 1.3 }}>
                  Appointment Date/Time
                </Typography>
                <Typography
                  component="p"
                  sx={{ m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
                  {appointment}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, pb: 1.75 }}>
                <Typography
                  component="p"
                  sx={{ mb: 0.5, color: "#666", fontSize: 12, lineHeight: 1.3 }}>
                  Service address
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    color: "#171717",
                    fontSize: 14,
                    lineHeight: 1.4,
                    overflowWrap: "anywhere",
                  }}>
                  {orderDetails.addressInfo?.serviceAddress || "—"}
                </Typography>
              </Box>
            </Stack>

            {orderDetails.orderReschedule === true && (
              <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #e7e7e7" }}>
                <Link
                  component="button"
                  type="button"
                  underline="none"
                  onClick={handleReschedule}
                  sx={{ color: "#006ce5", fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
                  NEED TO RESCHEDULE?
                </Link>
              </Box>
            )}
          </>
        )}
        {showMap && (
          <TechnicianLocationMap
            latitude={latitude}
            longitude={longitude}
            homeLatitude={homeLatitude}
            homeLongitude={homeLongitude}
            estimatedArrivalTime={estimatedArrivalTime}
            estimatedTravelTime={estimatedTravelTime}
            isTechArrived={isTechArrived}
          />
        )}
      </Box>
    </Box>
  );
};
